import { Link } from "react-router-dom";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { useConcierge } from "@/context/ConciergeContext";
import { DIGITAL_SERVICES, PROCESS, PROJECTS, IMAGES, FAQS } from "@/data/site";
import { CheckCircle, Sparkle } from "@phosphor-icons/react";
import { track } from "@/lib/api";
import { useSEO, serviceLd, breadcrumbLd, faqLd, localBusinessLd } from "@/lib/seo";

export default function DigitalAI() {
  const { openConcierge } = useConcierge();

  useSEO({
    title: "Web Design, Marketing & AI — Calgary & Canada-Wide | MRE",
    description:
      "Branding, web design, online marketing and practical AI for Canadian businesses — based in Calgary, working nationwide. Grow the business you just built. Get a free strategy consultation.",
    path: "/digital",
    image: IMAGES.growPanel,
    jsonLd: [
      serviceLd("Digital Marketing & AI", "Branding, web design, online marketing and AI/data services for small and mid-sized businesses across Canada."),
      localBusinessLd(),
      faqLd(FAQS),
      breadcrumbLd([{ name: "Home", path: "/" }, { name: "Digital & AI", path: "/digital" }]),
    ],
  });

  const digitalProjects = PROJECTS.filter((p) => p.discipline === "Digital");

  return (
    <div data-testid="digital-page">
      <PageHero
        overline="Digital & AI"
        title="Grow the business you just built."
        intro="Branding, web design, online marketing and practical AI — the digital presence that turns a finished space into a growing business."
        image={IMAGES.growPanel}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Digital & AI", path: "/digital" }]}
      />

      {/* Services */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <SectionHeading overline="What we grow" title="Four engines for growth." />
        <div className="grid sm:grid-cols-2 mt-12 border-t border-l border-border">
          {DIGITAL_SERVICES.map((s, i) => (
            <Reveal key={s.id} as={Link} to={`/services/${s.service}`} delay={i * 80} id={s.id} className="group block border-b border-r border-border p-8 lg:p-10 hover:bg-secondary/40 transition-colors">
              <span className="font-mono-tech text-xs tracking-widest text-primary">0{i + 1}</span>
              <h3 className="font-display uppercase tracking-tight text-3xl mt-3 group-hover:text-primary transition-colors">{s.title}</h3>
              <p className="mt-4 text-muted-foreground leading-relaxed">{s.desc}</p>
              <ul className="mt-6 space-y-3">
                {s.points.map((p) => (
                  <li key={p} className="flex items-center gap-3 text-sm text-foreground">
                    <CheckCircle size={18} className="text-primary shrink-0" weight="light" /> {p}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </section>

      {/* AI spotlight */}
      <section className="border-y border-border bg-secondary/20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <SectionHeading
              overline="AI in action"
              title="The concierge on this site? We build those."
              intro="The instant estimator you've been seeing is a working example of the AI tools we build for clients — lead concierges, dashboards and automations that capture business while you sleep."
            />
            <button
              onClick={() => {
                track("cta_click", { cta: "open_concierge", location: "digital_ai_spotlight" });
                openConcierge();
              }}
              data-testid="digital-try-concierge"
              className="mt-8 inline-flex items-center gap-3 bg-primary text-primary-foreground font-mono-tech text-xs tracking-widest uppercase px-7 h-12 hover:bg-[hsl(16,100%,55%)] transition-colors"
            >
              <Sparkle size={16} weight="fill" /> Try the live demo
            </button>
          </div>
          <div className="lg:col-span-6">
            <div className="relative border border-border overflow-hidden">
              <img src={IMAGES.ai} alt="AI and data dashboard work by MRE Developments" loading="lazy" className="w-full aspect-[4/3] object-cover" />
              <div className="absolute inset-0 blueprint-grid-fine opacity-30" />
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <SectionHeading overline="The launch process" title="From brief to live." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 mt-12 border-t border-l border-border">
          {PROCESS.map((p, i) => (
            <Reveal key={p.no} delay={i * 80} className="border-b border-r border-border p-8">
              <span className="font-display text-5xl text-primary">{p.no}</span>
              <h3 className="font-display uppercase tracking-tight text-2xl mt-4">{p.title}</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">{p.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Work */}
      <section className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <SectionHeading overline="Brand & digital work" title="Recent launches." />
          <div className="grid sm:grid-cols-2 gap-px bg-border border border-border mt-12">
            {digitalProjects.map((p) => (
              <Link key={p.id} to={`/services/${p.service}`} className="group relative bg-background overflow-hidden" data-testid={`digital-project-${p.id}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.image} alt={`${p.title} — ${p.category}`} loading="lazy" className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                </div>
                <div className="p-6">
                  <span className="font-mono-tech text-[10px] tracking-widest uppercase text-primary">{p.category} · {p.year}</span>
                  <h3 className="font-display uppercase tracking-tight text-xl mt-1">{p.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{p.summary}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection title="Let's grow your business." subtitle="Free strategy consultation. Clear scope, transparent pricing, measurable results." />
    </div>
  );
}
